import React, { useState } from 'react';
import styles from './Calculator.module.css';
import { useTranslation } from 'react-i18next';

const MAX_YEARS = 60;
const MAX_MONTHS = MAX_YEARS * 12;

function Calculator({ data, onChange, currencies, className }) {
  const { t } = useTranslation();
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let cleanValue = value;
    if (value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
      cleanValue = value.replace(/^0+/, '');
    }
    
    if (name === 'timeFrame') {
      const numValue = parseFloat(cleanValue || '0');
      const maxLimit = data.timeUnit === 'years' ? MAX_YEARS : MAX_MONTHS;
      
      if (numValue > maxLimit) {
        setError(`${t('calculator.cannotExceed')} ${maxLimit} ${data.timeUnit === 'years' ? t('calculator.years') : t('calculator.months')}`);
        return;
      }
      setError('');
      
      onChange({
        ...data,
        [name]: numValue
      });
      return;
    }

    const numValue = cleanValue === '' ? 0 : parseFloat(cleanValue);
    onChange({
      ...data,
      [name]: numValue
    });
  };

  const handleUnitChange = (unit) => {
    let newTimeFrame;
    if (unit === 'years') {
      newTimeFrame = Math.min(Math.round(data.timeFrame / 12), MAX_YEARS);
    } else {
      newTimeFrame = Math.min(data.timeFrame * 12, MAX_MONTHS);
    }
    
    setError('');
    onChange({
      ...data,
      timeUnit: unit,
      timeFrame: newTimeFrame
    });
  };

  // 格式化货币选项的显示
  const formatCurrencyOption = (code, currency) => {
    const currencyName = t(`currencies.${code}.name`);
    const countryName = t(`currencies.${code}.country`);
    return `${currency.symbol} ${currencyName} (${countryName})`;
  };

  return (
    <div className={`${styles.configurationContainer} ${className || ''}`}>
      <div className={styles.inputGroup}>
        <label>{t('calculator.currency')}</label>
        <select
          className={styles.currencySelect}
          value={data.currency}
          onChange={(e) => onChange({ ...data, currency: e.target.value })}
        >
          {Object.entries(currencies).map(([code, currency]) => (
            <option key={code} value={code}>
              {formatCurrencyOption(code, currency)}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.inputGroup}>
        <label>{t('calculator.initialDeposit')}</label>
        <div className={styles.inputWrapper}>
          <span className={styles.prefix}>{currencies[data.currency].symbol}</span>
          <input
            type="number"
            name="initialDeposit"
            value={data.initialDeposit || ''}
            onChange={handleInputChange}
            min="0"
            step="any"
          />
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label>{t('calculator.apy')}</label>
        <div className={styles.inputWrapper}>
          <input
            type="number"
            name="apy"
            value={data.apy || ''}
            onChange={handleInputChange}
            min="0"
            step="any"
          />
          <span className={styles.suffix}>%</span>
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label>{t('calculator.monthlyContribution')}</label>
        <div className={styles.inputWrapper}>
          <span className={styles.prefix}>{currencies[data.currency].symbol}</span>
          <input
            type="number"
            name="monthlyContribution"
            value={data.monthlyContribution || ''}
            onChange={handleInputChange}
            min="0"
            step="any"
          />
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label>{t('calculator.timeToSave')}</label>
        <div className={styles.timeInputContainer}>
          <div className={styles.inputWrapper}>
            <input
              type="number"
              name="timeFrame"
              value={data.timeFrame === 0 ? '' : data.timeFrame}
              onChange={handleInputChange}
              min="0"
              max={data.timeUnit === 'years' ? MAX_YEARS : MAX_MONTHS}
            />
          </div>
          <div className={styles.unitToggle}>
            <button
              className={`${styles.unitButton} ${data.timeUnit === 'years' ? styles.active : ''}`}
              onClick={() => handleUnitChange('years')}
            >
              {t('calculator.years')}
            </button>
            <button
              className={`${styles.unitButton} ${data.timeUnit === 'months' ? styles.active : ''}`}
              onClick={() => handleUnitChange('months')}
            >
              {t('calculator.months')}
            </button>
          </div>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
      </div>
    </div>
  );
}

export default Calculator; 